import { Modal } from "@dropbox/dig-components/modal";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import { Twinkle2Line } from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { MarkdownResponse } from "@mirage/mosaics/Chat/components/chat/markdown/MarkdownResponse";
import { MODAL_ANIMATIONS } from "@mirage/mosaics/Layout/constants";
import {
  type AskWithPromptMultiDocsParams,
  CtaSource,
} from "@mirage/service-conversation/router_state_types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import { openURL } from "@mirage/service-platform-actions";
import useSearchQueryId from "@mirage/shared/hooks/useSearchQueryId";
import { RoutePath } from "@mirage/webapp/routeTypes";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { DocumentDetailsPanel } from "./DocumentDetailsPanel";
import { DocumentPreviewHeader } from "./DocumentPreviewHeader";
import { usePreviewModalAnalytics } from "./hooks/usePreviewModalAnalytics";
import { useDocumentSummary } from "./services/useDocumentSummary";
import styles from "./SummaryFirstDocumentPreviewModal.module.css";

import type { DocumentFollowUpSuggestion } from "@mirage/mosaics/QuerySuggestions/useDocumentFollowUpSuggestions";
import type { ResultActionConfig } from "@mirage/search/SearchResults/ResultRow/types";
import type { SearchResult } from "@mirage/service-dbx-api";

function handleOpenInNewTab(result: SearchResult) {
  if (result.url) {
    openURL(result.url);
  }
}

type SummaryFirstDocumentPreviewModalProps = {
  open: boolean;
  onClose: () => void;
  result: SearchResult;
  resultActionConfig?: ResultActionConfig;
};

export function SummaryFirstDocumentPreviewModal({
  open,
  onClose,
  result,
  resultActionConfig,
}: SummaryFirstDocumentPreviewModalProps) {
  const { t } = useTranslation();
  const { searchSessionManager } = useMirageAnalyticsContext();
  const searchSessionId = searchSessionManager.getSessionIdOrUndefined();
  const { searchQueryUuid } = useSearchQueryId();
  const previewModalUuid = useRef(uuidv4());
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [previewWidth, setPreviewWidth] = useState(60);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { search: queryParams } = useLocation();

  const { summary, isLoading, error, conversationId, getSummary } =
    useDocumentSummary(result);

  const {
    logPanelResize,
    logOpenInProvider,
    logCopyLink,
    logAddToStack,
    logAskFollowUpQuestion,
    logFileDownloadComplete,
  } = usePreviewModalAnalytics({
    isOpen: open,
    searchSessionId,
    connector: result.connectorInfo.connectorId,
    resultUuid: result.uuid,
    dashActionSurface: "serp",
    previewModalUuid: previewModalUuid.current,
  });

  useEffect(() => {
    if (open) {
      if (!isLoading && summary) {
        logFileDownloadComplete("success");
      } else if (!isLoading && error) {
        logFileDownloadComplete("error");
      }
    }
  }, [isLoading, summary, error, open, logFileDownloadComplete]);

  useEffect(() => {
    const isMediaFile =
      result.fileTypeInfo?.id === "video" ||
      result.fileTypeInfo?.id === "image" ||
      result.fileTypeInfo?.id === "audio";

    if (open && result) {
      if (isMediaFile && result.url) {
        openURL(result.url);
        onClose();
        return;
      }
      getSummary();
    }
  }, [open, result.uuid, onClose]);

  const handleSuggestionClick = useCallback(
    (suggestion: DocumentFollowUpSuggestion) => {
      logAskFollowUpQuestion();
      const askWithPromptParams: AskWithPromptMultiDocsParams = {
        chatSources: [{ type: "mirage_search_result", searchResult: result }],
        message: suggestion.prompt,
        scenarioMeta: {
          type: "search",
          query: queryParams,
          searchSessionId,
          searchQueryUuid,
        },
      };

      const routerState = buildConversationRouterState(
        CtaSource.DocumentPreviewSuggestion,
        askWithPromptParams,
      );
      navigate(RoutePath.CHAT, { state: routerState });
      onClose();
    },
    [
      logAskFollowUpQuestion,
      searchSessionId,
      searchQueryUuid,
      result,
      queryParams,
      navigate,
      onClose,
    ],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const newWidth =
          ((moveEvent.clientX - containerRect.left) / containerRect.width) *
          100;

        const constrainedWidth = Math.min(Math.max(newWidth, 45), 80);
        setPreviewWidth(constrainedWidth);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        logPanelResize(previewWidth);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [previewWidth, logPanelResize],
  );

  const handleOpenInProvider = useCallback(() => {
    logOpenInProvider();
    const openLinkAction = resultActionConfig?.displayActions?.find(
      (action) => action.key === "open_link",
    );
    openLinkAction?.onClick?.("click");
  }, [logOpenInProvider, resultActionConfig]);

  const handleCopyLink = useCallback(() => {
    logCopyLink();
    const copyAction = resultActionConfig?.displayActions?.find(
      (action) => action.key === "copy",
    );
    copyAction?.onClick?.("click");
  }, [logCopyLink, resultActionConfig]);

  const handleAddToStack = useCallback(() => {
    logAddToStack();
    const addToStackAction = resultActionConfig?.displayActions?.find(
      (action) => action.key === "add_to_stack",
    );
    addToStackAction?.onClick?.("click");
  }, [logAddToStack, resultActionConfig]);

  const handleErrorClick = useCallback(() => {
    handleOpenInNewTab(result);
  }, [result]);

  const renderedSummary = useMemo(() => {
    return (
      <MarkdownResponse
        markdownText={summary || ""}
        logComposeEvent={() => {}}
        conversationId={conversationId || undefined}
        searchSessionId={searchSessionId || undefined}
      />
    );
  }, [summary, conversationId, searchSessionId]);

  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      className={styles.modalWindow}
      width="large"
      aria-label={t("document_preview_modal_label")}
    >
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className={styles.headerMotion}
              {...MODAL_ANIMATIONS.header}
            >
              <DocumentPreviewHeader
                result={result}
                onClose={onClose}
                onOpenInProvider={handleOpenInProvider}
                onCopyLink={handleCopyLink}
                onAddToStack={handleAddToStack}
                resultActionConfig={resultActionConfig}
              />
            </motion.div>
            <motion.div
              className={styles.bodyMotion}
              {...MODAL_ANIMATIONS.body}
            >
              <Modal.Body className={styles.body}>
                <div
                  className={classNames(styles.contentContainer, {
                    [styles.dragging]: isDragging,
                  })}
                  ref={containerRef}
                  style={
                    {
                      "--preview-width": `${previewWidth}%`,
                      "--details-width": `${100 - previewWidth}%`,
                    } as React.CSSProperties
                  }
                >
                  {isDragging && <div className={styles.dragOverlay} />}

                  <div className={styles.summaryContainer}>
                    <div className={styles.summaryHeader}>
                      <UIIcon src={Twinkle2Line} size="small" />
                      <Text size="small" color="subtle">
                        {t("summary_header_preview_modal")}
                      </Text>
                    </div>
                    {isLoading ? (
                      <div className={styles.loadingState}>
                        <div className={styles.summaryLoadingContainer}>
                          <UIIcon src={Twinkle2Line} size="small" />
                          <div className={styles.summaryLoadingText}>
                            {t("doc_summary_qna_loading_summary")}
                          </div>
                        </div>
                      </div>
                    ) : error ? (
                      <div className={styles.errorState}>
                        <button
                          onClick={handleErrorClick}
                          className={styles.errorLink}
                        >
                          {t("failed_doc_summary_error")}
                        </button>
                      </div>
                    ) : (
                      <div className={styles.summary}>{renderedSummary}</div>
                    )}
                  </div>

                  <button
                    className={classNames(styles.resizeHandle, {
                      [styles.dragging]: isDragging,
                    })}
                    onMouseDown={handleMouseDown}
                    aria-label={t("resize_preview_area_label")}
                  />

                  <DocumentDetailsPanel
                    result={result}
                    showMoreDetails={showMoreDetails}
                    onToggleDetails={() => setShowMoreDetails(!showMoreDetails)}
                    onSuggestionClick={handleSuggestionClick}
                    isSummaryFirst={true}
                  />
                </div>
              </Modal.Body>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </Modal>
  );
}
